import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { ShieldCheck, Loader2 } from "lucide-react";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { api, errMsg } from "@/lib/api";

let configCache = null;
const getConfig = async () => (configCache ||= (await api.get("/payments/config")).data);

const MockPay = ({ order, done }) => {
  const [busy, setBusy] = useState(false);
  const pay = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/payments/create", order);
      await api.post(`/payments/${data.payment_id}/capture`);
      done();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <>
      <div className="rounded-xl border border-[#FFB800]/40 bg-[#FFB800]/10 p-3 font-mono text-[11px] uppercase tracking-widest text-[#FFB800]" data-testid="pay-mock-banner">Test mode — simulated payment.</div>
      <button data-testid="pay-confirm-button" onClick={pay} disabled={busy} className="flex w-full items-center justify-center gap-2 rounded-full bg-[#FFC439] py-3.5 font-display font-extrabold text-[#003087] disabled:opacity-60">
        {busy && <Loader2 className="h-5 w-5 animate-spin" />}Pay with PayPal
      </button>
    </>
  );
};

const RealPay = ({ cfg, order, done }) => {
  const pid = useRef(null);
  const isPlan = order.kind === "plan";
  const start = async () => {
    const { data } = await api.post("/payments/create", order);
    pid.current = data.payment_id;
    return data;
  };
  const fail = (e) => toast.error(errMsg(e) || "PayPal error");
  const options = isPlan
    ? { clientId: cfg.client_id, currency: "EUR", intent: "subscription", vault: true, components: "buttons" }
    : { clientId: cfg.client_id, currency: "EUR", intent: "capture", components: "buttons" };
  return (
    <div data-testid="paypal-buttons" className="rounded-xl bg-white p-3">
      <PayPalScriptProvider options={options}>
        {isPlan ? (
          <PayPalButtons style={{ layout: "vertical", label: "subscribe", shape: "pill" }}
            createSubscription={async (_d, actions) => { await start(); return actions.subscription.create({ plan_id: cfg.plans[order.period === "year" ? `${order.item}_year` : order.item] }); }}
            onApprove={async (d) => { try { await api.post("/payments/subscription/confirm", { payment_id: pid.current, subscription_id: d.subscriptionID }); done(); } catch (e) { fail(e); } }}
            onError={fail} />
        ) : (
          <PayPalButtons style={{ layout: "vertical", shape: "pill" }}
            createOrder={async () => (await start()).order_id}
            onApprove={async () => { try { await api.post(`/payments/${pid.current}/capture`); done(); } catch (e) { fail(e); } }}
            onError={fail} />
        )}
      </PayPalScriptProvider>
    </div>
  );
};

export const PayDialog = ({ order, onClose, onPaid }) => {
  const [cfg, setCfg] = useState(configCache);
  useEffect(() => { if (order && !cfg) getConfig().then(setCfg).catch(() => toast.error("Payments unavailable")); }, [order, cfg]);
  const done = () => { toast.success("Payment completed", { description: `${order.label} — €${order.price}` }); onPaid?.(); onClose(); };
  return (
    <Dialog open={!!order} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] overflow-y-auto border-white/10 bg-[#0F1420] text-white" data-testid="pay-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Checkout</DialogTitle>
          <DialogDescription className="text-slate-400">{order?.label}</DialogDescription>
        </DialogHeader>
        <div className="flex items-end justify-between border-y border-white/5 py-5">
          <span className="text-slate-400">Total</span>
          <span className="led text-4xl font-bold" data-testid="pay-amount">€{order?.price}{order?.kind === "plan" ? <span className="text-sm text-slate-400">{order.period === "year" ? "/year" : "/mo"}</span> : null}</span>
        </div>
        {!cfg ? <Loader2 className="mx-auto h-6 w-6 animate-spin text-slate-400" /> : cfg.mode === "paypal" ? <RealPay cfg={cfg} order={order} done={done} /> : <MockPay order={order} done={done} />}
        <p className="flex items-center justify-center gap-2 text-xs text-slate-500"><ShieldCheck className="h-4 w-4" />{order?.kind === "plan" ? `${order?.period === "year" ? "Yearly" : "Monthly"} subscription via PayPal. Cancel anytime from your dashboard.` : "One-time secure payment via PayPal."}</p>
      </DialogContent>
    </Dialog>
  );
};
